import { useEffect, useMemo, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { Plus, Pencil, Trash2, Banknote, Coins, Search } from "lucide-react";
import { useAccounts, useCreateSnapshot, useDeleteSnapshot, useImbalancePreview, useSnapshots, useUpdateSnapshot } from "@/api/queries";
import { ErrorState } from "@/components/ErrorState";
import { Modal } from "@/components/Modal";
import { Currency, AccountOption } from "@/components/Currency";
import { Select } from "@/components/Select";
import { cn, formatAmount, formatDate, todayLocal, useDraftId } from "@/lib/utils";
import type { Account, Snapshot } from "@/api/types";

const todayISO = todayLocal;   // SPEC-024: дефолт даты снапшота — локальный день, не UTC

export function SnapshotsPage() {
    const { data: snapData, isLoading, isError, refetch } = useSnapshots();
    const { data: accData } = useAccounts();
    const create = useCreateSnapshot();
    const update = useUpdateSnapshot();
    const remove = useDeleteSnapshot();

    const accounts = accData?.accounts ?? [];
    const accById = useMemo(() => new Map(accounts.map(a => [a.id, a])), [accounts]);
    const snapshots = snapData?.snapshots ?? [];

    // ADM-10: /accounts передаёт ?account_id=… (validateSearch в routeTree) — применяем как фильтр.
    const { account_id: searchAccountId } = useSearch({ strict: false }) as { account_id?: string };

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<Snapshot | null>(null);
    const [filterAccount, setFilterAccount] = useState<string>(searchAccountId ?? "");
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (searchAccountId) setFilterAccount(searchAccountId);
    }, [searchAccountId]);

    const filtered = useMemo(() => {
        let rows = snapshots;
        if (filterAccount) rows = rows.filter(s => s.account_id === filterAccount);
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(s =>
                (s.note?.toLowerCase().includes(q) ?? false) ||
                String(s.amount).includes(q) ||
                (accById.get(s.account_id)?.name.toLowerCase().includes(q) ?? false),
            );
        }
        return rows;
    }, [snapshots, filterAccount, search, accById]);

    const cols = filterAccount ? 6 : 5;   // SPEC-050: колонка невязки только при фильтре по ведру
    const openCreate = () => { setEditing(null); setModalOpen(true); };
    const openEdit = (s: Snapshot) => { setEditing(s); setModalOpen(true); };

    const handleDelete = async (s: Snapshot) => {
        const acc = accById.get(s.account_id);
        if (!confirm(`Удалить снапшот?\n${acc?.name ?? s.account_id} · ${formatAmount(s.amount, acc?.currency ?? "")} · ${formatDate(s.date)}`)) return;
        await remove.mutateAsync(s.id);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight">Снапшоты</h1>
                    <p className="text-muted-foreground mt-1">
                        Каждый снапшот — это фиксация баланса одного ведра на конкретную дату. Заводи при зарплате, обмене, любом значимом изменении.
                    </p>
                </div>
                <button onClick={openCreate} className="btn-primary px-4 py-2 self-start">
                    <Plus className="h-4 w-4" /> Новый снапшот
                </button>
            </div>

            <div className="card p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                        type="search"
                        placeholder="Поиск по описанию, ведру, сумме…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                </div>
                <Select
                    value={filterAccount}
                    onChange={e => setFilterAccount(e.target.value)}
                    wrapperClassName="min-w-[12rem]"
                    aria-label="Фильтр по ведру"
                >
                    <option value="">Все вёдра</option>
                    {accounts.map(a => <AccountOption key={a.id} account={a} />)}
                </Select>
            </div>

            <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/50 border-b">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-32">Дата</th>
                                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Ведро</th>
                                <th className="text-right px-4 py-3 font-medium text-muted-foreground w-44">Сумма</th>
                                {/* SPEC-050: невязка показывается только при фильтре по ведру —
                                    в общем списке колонка из разных валют нечитаема. */}
                                {filterAccount && <th className="text-right px-4 py-3 font-medium text-muted-foreground w-40">Расчёт / невязка</th>}
                                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Описание</th>
                                <th className="px-4 py-3 w-20"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr><td colSpan={cols} className="px-4 py-12 text-center text-muted-foreground">Загрузка…</td></tr>
                            )}
                            {isError && (
                                <tr><td colSpan={cols} className="px-4 py-8"><ErrorState onRetry={() => refetch()} label="Не удалось загрузить снапшоты" /></td></tr>
                            )}
                            {!isLoading && !isError && filtered.length === 0 && (
                                <tr><td colSpan={cols} className="px-4 py-12 text-center text-muted-foreground">
                                    Снапшотов пока нет. Заведи первый — кнопкой выше.
                                </td></tr>
                            )}
                            {filtered.map(s => {
                                const acc = accById.get(s.account_id);
                                const isCash = acc?.form === "cash";
                                const Icon = isCash ? Banknote : Coins;
                                const isAuto = s.source === "auto_transaction";
                                return (
                                    <tr key={s.id} className="border-b last:border-b-0 hover:bg-secondary/30 transition-colors">
                                        <td className="px-4 py-2.5 num text-muted-foreground whitespace-nowrap">{formatDate(s.date)}</td>
                                        <td className="px-4 py-2.5">
                                            <span className="inline-flex items-center gap-2">
                                                <span
                                                    className="h-6 w-6 rounded grid place-items-center"
                                                    style={{ background: (acc?.color ?? "#9ca3af") + "22", color: acc?.color ?? "currentColor" }}
                                                >
                                                    <Icon className="h-3.5 w-3.5" />
                                                </span>
                                                <span>{acc?.name ?? s.account_id}</span>
                                                {isAuto && (
                                                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/15 text-primary font-medium"
                                                          title="Создан автоматически при обмене/переводе. Редактировать нельзя — изменится через удаление транзакции.">
                                                        auto
                                                    </span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-right num font-medium tabular-nums whitespace-nowrap">
                                            {formatAmount(s.amount, acc?.currency ?? "")} <Currency code={acc?.currency} />
                                        </td>
                                        {filterAccount && (
                                            <td className="px-4 py-2.5 text-right num tabular-nums whitespace-nowrap text-xs">
                                                {s.imbalance == null ? (
                                                    <span className="text-muted-foreground" title="журнал невязок не считался: снапшот раньше даты доверия либо не manual">—</span>
                                                ) : (
                                                    <>
                                                        <span className="text-muted-foreground">{formatAmount(s.expected_amount ?? 0, acc?.currency ?? "")}</span>
                                                        {" · "}
                                                        <span className={cn("font-medium",
                                                            Math.abs(s.imbalance) < 0.01 ? "text-muted-foreground"
                                                                : s.imbalance > 0 ? "text-positive" : "text-negative")}
                                                            title={s.imbalance_kind === "investment_mark" ? "переоценка/начисление инвест-ведра"
                                                                : s.imbalance_kind === "baseline_anchor" ? "первый интервал — в сводки не входит"
                                                                : "неучтённые потоки"}>
                                                            {Math.abs(s.imbalance) < 0.01 ? "0" : `${s.imbalance > 0 ? "+" : ""}${formatAmount(s.imbalance, acc?.currency ?? "")}`}
                                                        </span>
                                                        {s.imbalance_kind === "baseline_anchor" && <span className="text-muted-foreground"> ·опора</span>}
                                                    </>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-4 py-2.5">
                                            {s.note
                                                ? <span>{s.note}</span>
                                                : <span className="text-muted-foreground italic">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                            {!isAuto && (
                                                <>
                                                    <button onClick={() => openEdit(s)} className="btn-icon" aria-label="Редактировать">
                                                        <Pencil className="h-4 w-4" />
                                                    </button>
                                                    {/* ADM-19: guard от двойного клика — пока DELETE в полёте, кнопки строк неактивны */}
                                                    <button onClick={() => handleDelete(s)} disabled={remove.isPending} className="btn-icon text-destructive" aria-label="Удалить">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <SnapshotModal
                open={modalOpen}
                editing={editing}
                accounts={accounts}
                onClose={() => setModalOpen(false)}
                onSubmit={async (payload, id) => {
                    if (id) await update.mutateAsync({ id, patch: payload });
                    else await create.mutateAsync(payload);
                    setModalOpen(false);
                }}
            />
        </div>
    );
}

interface SnapshotModalProps {
    open: boolean;
    editing: Snapshot | null;
    accounts: Account[];
    onClose: () => void;
    onSubmit: (payload: { id?: string; date: string; account_id: string; amount: number; note: string | null }, id?: string) => Promise<void>;
}

function SnapshotModal({ open, editing, accounts, onClose, onSubmit }: SnapshotModalProps) {
    const draftId = useDraftId(open && !editing);   // ADM-02 (SPEC-044): id create-записи на одно открытие формы
    const [date, setDate] = useState(editing?.date ?? todayISO());
    const [accountId, setAccountId] = useState(editing?.account_id ?? accounts[0]?.id ?? "");
    const [amount, setAmount] = useState<string>(editing ? String(editing.amount) : "");
    const [note, setNote] = useState(editing?.note ?? "");
    const [submitting, setSubmitting] = useState(false);

    // ре-инициализация при открытии (ADM-14: useEffect, не useMemo — setState в memo
    // был side-effect-анти-паттерном и расходился с остальными модалами)
    useEffect(() => {
        if (!open) return;
        setDate(editing?.date ?? todayISO());
        setAccountId(editing?.account_id ?? accounts[0]?.id ?? "");
        setAmount(editing ? String(editing.amount) : "");
        setNote(editing?.note ?? "");
        setSubmitting(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editing?.id]);

    // ADM-14: модал мог открыться до загрузки accounts — accountId="" при непустом списке
    // опций (браузер рисует первую опцию выбранной, state пуст → кнопка disabled без
    // объяснения). Как только accounts пришли — доводим до дефолта.
    useEffect(() => {
        if (open && !editing && !accountId && accounts.length > 0) setAccountId(accounts[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, accounts.length]);

    const selectedAcc = accounts.find(a => a.id === accountId);
    const lastForAcc = selectedAcc?.manual_snapshot ?? selectedAcc?.latest_snapshot;

    // FIN-01-хвост (SPEC-044): валюта снапшота = валюта ведра. В edit полный payload
    // несёт старый amount — смена ведра на другую валюту обходила бы серверный guard
    // («100 000 RUB» молча стали бы «100 000 EUR»). Чистим сумму — вводится заново.
    const originalCcy = editing ? accounts.find(a => a.id === editing.account_id)?.currency : null;
    const currencyChanged = !!originalCcy && !!selectedAcc && selectedAcc.currency !== originalCcy;
    useEffect(() => {
        if (currencyChanged) setAmount("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAcc?.currency]);

    const valid = !!date && !!accountId && parseFloat(amount) >= 0;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!valid) return;
        setSubmitting(true);
        try {
            await onSubmit(
                {
                    ...(editing ? {} : { id: draftId }),   // ADM-02: только create, не PUT
                    date, account_id: accountId, amount: parseFloat(amount), note: note.trim() || null,
                },
                editing?.id,
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title={editing ? "Редактировать снапшот" : "Новый снапшот"}>
            <form onSubmit={submit} className="space-y-4">
                <Field label="Ведро">
                    <Select fullWidth value={accountId} onChange={e => setAccountId(e.target.value)}>
                        {/* ADM-14: пока state пуст, показываем честный плейсхолдер вместо
                            визуально «выбранной» первой опции при пустом value */}
                        {!accountId && <option value="">— выбери ведро —</option>}
                        {accounts.map(a => (
                            <AccountOption key={a.id} account={a} />
                        ))}
                    </Select>
                </Field>

                <div className="grid grid-cols-[1fr_auto] gap-3">
                    <Field label="Сумма">
                        <div className="relative">
                            <input
                                type="number"
                                inputMode="decimal"
                                step="any"
                                min="0"
                                value={amount}
                                onChange={e => setAmount(e.target.value)}
                                placeholder="0"
                                className="w-full px-3 py-2 pr-20 rounded-lg border bg-background text-base tabular-nums focus:outline-none focus:ring-2 focus:ring-ring [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2">
                                <Currency code={selectedAcc?.currency} />
                            </span>
                        </div>
                        {currencyChanged && (
                            <span className="block mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                                валюта сменилась на {selectedAcc!.currency} — введи сумму заново
                            </span>
                        )}
                    </Field>
                    <Field label="Дата">
                        <input
                            type="date"
                            value={date}
                            onChange={e => setDate(e.target.value)}
                            className="px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </Field>
                </div>

                {lastForAcc && (
                    <div className="text-xs text-muted-foreground bg-secondary/40 rounded-lg p-3 -mt-1">
                        Прошлый: <span className="num tabular-nums">{formatAmount(lastForAcc.amount, selectedAcc?.currency ?? "")}</span> <Currency code={selectedAcc?.currency} size="xs" /> от {formatDate(lastForAcc.date)}
                        {amount && (
                            <>
                                {" · "}
                                <span className={cn("font-medium", parseFloat(amount) - lastForAcc.amount > 0 ? "text-positive" : parseFloat(amount) - lastForAcc.amount < 0 ? "text-negative" : "")}>
                                    {parseFloat(amount) - lastForAcc.amount > 0 ? "+" : ""}
                                    {formatAmount(parseFloat(amount) - lastForAcc.amount, selectedAcc?.currency ?? "")}
                                </span>
                            </>
                        )}
                    </div>
                )}

                {/* SPEC-050: невязка до сохранения — «расчётно X, вводишь Y, разница Z».
                    Расчёт идёт от ПРЕДЫДУЩЕГО снапшота плюс события интервала, поэтому
                    он не равен строке «Прошлый» выше: там просто разница двух снимков. */}
                <ImbalanceHint
                    accountId={accountId}
                    date={date}
                    amount={amount === "" ? null : parseFloat(amount)}
                    excludeId={editing?.id}
                    currency={selectedAcc?.currency ?? ""}
                />

                <Field label="Описание (необязательно)">
                    <input
                        type="text"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="напр. зарплата, обмен RUB→EUR, подарок"
                        className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                </Field>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-ghost px-4 py-2">Отмена</button>
                    <button type="submit" disabled={!valid || submitting} className="btn-primary px-4 py-2 min-w-[7rem]">
                        {submitting ? "…" : (editing ? "Сохранить" : "Создать")}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

/**
 * SPEC-050: подсказка невязки в форме снапшота.
 *
 * Расчётный баланс — это «сколько должно быть по записям»: предыдущий снапшот плюс
 * все события интервала. Разница с вводимой суммой и есть невязка: для обычного
 * ведра — незаписанные потоки, для инвестиционного — рыночная переоценка и
 * начисления (там разница нормальна и тратами не является).
 */
function ImbalanceHint({ accountId, date, amount, excludeId, currency }: {
    accountId: string; date: string; amount: number | null; excludeId?: string; currency: string;
}) {
    const { data, isFetching, isError } = useImbalancePreview({ accountId, date, amount, excludeId });
    if (amount == null || !accountId || !date) return null;
    if (isError) return null;                          // подсказка не должна ломать ввод
    if (!data) return (
        <div className="text-xs text-muted-foreground -mt-1 px-3">{isFetching ? "считаю расчётный баланс…" : null}</div>
    );

    const isInvest = data.kind === "investment_mark";
    const isAnchor = data.kind === "baseline_anchor";
    const label = isInvest ? "переоценка/начисление" : "невязка";
    const zero = Math.abs(data.imbalance) < 0.01;

    // Первого снапшота ведра сравнивать не с чем: расчёт идёт от нуля, и «невязка»
    // равнялась бы всему остатку. Показывать такое числом — пугать пользователя
    // величиной, которая ничего не значит.
    if (!data.prev) {
        return (
            <div className="text-xs rounded-lg p-3 -mt-1 bg-secondary/40 text-muted-foreground">
                Первый снапшот этого ведра — сравнивать не с чем. Невязка начнёт считаться со следующего.
            </div>
        );
    }

    return (
        <div className="text-xs rounded-lg p-3 -mt-1 bg-secondary/40">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-muted-foreground">Расчётно:</span>
                <span className="num tabular-nums">{formatAmount(data.expected, currency)}</span>
                <Currency code={currency} size="xs" />
                <span className="text-muted-foreground">· вводишь</span>
                <span className="num tabular-nums">{formatAmount(data.actual, currency)}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">{label}</span>
                <span className={cn("num tabular-nums font-medium",
                    zero ? "" : data.imbalance > 0 ? "text-positive" : "text-negative")}>
                    {zero ? "0" : `${data.imbalance > 0 ? "+" : ""}${formatAmount(data.imbalance, currency)}`}
                </span>
                {data.imbalance_eur != null && !zero && currency !== "EUR" && (
                    <span className="text-muted-foreground">({data.imbalance_eur > 0 ? "+" : ""}{Math.round(data.imbalance_eur)} €)</span>
                )}
            </div>
            <div className="mt-1 text-muted-foreground">
                от снапшота {formatDate(data.prev.date)}, событий в интервале: {data.events_count}
                {isAnchor && " · интервал до даты доверия, в сводки не идёт"}
                {isInvest && " · для инвест-ведра это доходность, а не пропущенная трата"}
            </div>
        </div>
    );
}

interface FieldProps { label: string; children: React.ReactNode }
function Field({ label, children }: FieldProps) {
    return (
        <label className="block">
            <span className="text-sm text-muted-foreground block mb-1.5">{label}</span>
            {children}
        </label>
    );
}
